/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Document Reassembly DTOs & OpenAPI Schemas (Task 8.5.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from 'zod';

export const ReassemblyStatsSchema = z
  .object({
    totalChunks: z.number().int().nonnegative().describe('Total number of chunks evaluated'),
    translatedChunks: z.number().int().nonnegative().describe('Number of chunks with valid translation applied'),
    fallbackChunks: z.number().int().nonnegative().describe('Number of chunks where source text was used as fallback'),
    skippedChunks: z.number().int().nonnegative().describe('Number of chunks omitted/skipped'),
    outputCharCount: z.number().int().nonnegative().describe('Total characters in the reconstructed document'),
    outputWordCount: z.number().int().nonnegative().describe('Total words in the reconstructed document'),
    unmaskedTagCount: z.number().int().nonnegative().describe('Total <ph id="..."/> tokens restored to original markup'),
  })
  .openapi('ReassemblyStats');

export const ReassembleDocumentRequestSchema = z
  .object({
    fallbackToSource: z
      .boolean()
      .optional()
      .default(true)
      .describe('Whether to retain source text if target translation is missing'),
    reviewedOnly: z
      .boolean()
      .optional()
      .default(false)
      .describe('If true, only reviewed/approved segments are included in the reassembled output'),
    unmaskTags: z
      .boolean()
      .optional()
      .default(true)
      .describe('Whether to restore <ph id="..."/> placeholder tokens back to original markup/variables'),
    useSourceTemplate: z
      .boolean()
      .optional()
      .default(true)
      .describe('Whether to reassemble into the original raw source text template if available'),
    cleanPunctuationSpacing: z
      .boolean()
      .optional()
      .default(true)
      .describe('Whether to format and clean language-specific punctuation spacing (e.g. French, CJK)'),
  })
  .openapi('ReassembleDocumentRequest');

export const ReassembleDocumentResponseSchema = z
  .object({
    translationId: z.string().describe('Target translation project ID'),
    content: z.string().describe('Reconstructed translated document text'),
    stats: ReassemblyStatsSchema,
    warnings: z.array(z.string()).describe('Non-fatal reassembly warnings or sequence gap notifications'),
  })
  .openapi('ReassembleDocumentResponse');

export type ReassemblyStatsDto = z.infer<typeof ReassemblyStatsSchema>;
export type ReassembleDocumentRequestDto = z.infer<typeof ReassembleDocumentRequestSchema>;
export type ReassembleDocumentResponseDto = z.infer<typeof ReassembleDocumentResponseSchema>;
